'use client'

import { SolidCircle } from '@qr-front/icons/solid-circle'
import { Button } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogDescription,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { DialogContent, DialogFooter } from '@qr-front/ui/components/Dialog'
import { cn } from '@qr-front/ui/utils'
import { useAtom, useAtomValue } from 'jotai'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { useRouter } from '@/i18n-routing'

import {
	bulkSelectedQrsState,
	confirmationDialogTypeState,
	qrItemState,
} from '../dialogsState'
import { TABLE_ACTION_TYPE } from '../TableActions/Actions/actionOptions'
import { tableAtom } from '../tableState'
import {
	actionTypeToConfirmationDialogConfig,
	bulkActionToConfirmationDialogConfig,
} from './confirmationDialogTranslations'

const BULK_ACTION_TYPES: TABLE_ACTION_TYPE[] = [
	TABLE_ACTION_TYPE.BulkPause,
	TABLE_ACTION_TYPE.BulkActivate,
	TABLE_ACTION_TYPE.BulkResetScans,
	TABLE_ACTION_TYPE.BulkDelete,
]

export const ConfirmationDialog = () => {
	const t = useTranslations()
	const locale = useLocale()
	const router = useRouter()

	const table = useAtomValue(tableAtom)
	const [qrItem, setQrItem] = useAtom(qrItemState)
	const [selectedQrs, setSelectedQrs] = useAtom(bulkSelectedQrsState)
	const [confirmationDialogType, setConfirmationDialogType] = useAtom(
		confirmationDialogTypeState,
	)
	const isBulkActionType =
		!!confirmationDialogType &&
		BULK_ACTION_TYPES.includes(confirmationDialogType)
	const isBulkAction = isBulkActionType && (selectedQrs?.length ?? 0) > 0

	const dialogConfig = useMemo(() => {
		if (!confirmationDialogType) return

		return isBulkAction
			? bulkActionToConfirmationDialogConfig[confirmationDialogType]
			: actionTypeToConfirmationDialogConfig[confirmationDialogType]
	}, [confirmationDialogType, isBulkAction])

	const bulkDialogConfig = useMemo(() => {
		if (!confirmationDialogType || !isBulkAction) return null
		return bulkActionToConfirmationDialogConfig[confirmationDialogType]
	}, [confirmationDialogType, isBulkAction])

	const individualDialogConfig = useMemo(() => {
		if (!confirmationDialogType || isBulkAction) return null
		return actionTypeToConfirmationDialogConfig[confirmationDialogType]
	}, [confirmationDialogType, isBulkAction])

	const handleOnConfirm = async () => {
		if (isBulkAction) {
			if (!bulkDialogConfig || !selectedQrs?.length) return
			await bulkDialogConfig.onConfirm(selectedQrs, router, t)
			setSelectedQrs([])
			table.resetRowSelection()
		} else {
			if (!individualDialogConfig || !qrItem?.id) return
			await individualDialogConfig.onConfirm(qrItem, router, t, locale)
			setQrItem(null)
		}

		setConfirmationDialogType(null)
	}

	const handleClose = () => {
		setQrItem(null)
		setConfirmationDialogType(null)
	}

	if (!dialogConfig) return null

	const selectedCount = selectedQrs?.length || 0

	return (
		<Dialog
			open={!!confirmationDialogType && (!!qrItem || selectedCount > 0)}
			onOpenChange={handleClose}
		>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="w-full p-6 tablet:w-[500px] tablet:p-8"
			>
				<div className="flex flex-col items-center gap-2">
					<DialogTitle className="text-center">
						{t(dialogConfig.title, { count: selectedCount })}
					</DialogTitle>
					{isBulkAction && (
						<>
							<DialogDescription className="text-sm text-gray-600 text-center">
								{t('dashboard__qr_table__items_selected', {
									size: selectedCount,
									count: selectedCount,
								})}
							</DialogDescription>
							<div className="my-2 w-full border-b border-border-grey-50" />
						</>
					)}
					<DialogDescription className="text-center">
						{t(dialogConfig.subtitle, { count: selectedCount })}
					</DialogDescription>
					<DialogDescription
						className={cn(
							dialogConfig.description !==
								'dashboard__change_qr_type__modal__description' &&
								'text-center',
							!isBulkAction &&
								dialogConfig.description !==
									'dashboard__change_qr_type__modal__description' &&
								'font-semibold',
						)}
					>
						{t.rich(dialogConfig.description, {
							count: selectedCount,
							li: (chunks) => (
								<li key={chunks?.toString()} className="flex gap-2">
									<SolidCircle className="mt-2 h-2 min-w-2 shrink-0" />
									{chunks}
								</li>
							),
						})}
					</DialogDescription>
				</div>

				<DialogFooter className={cn('mt-6 gap-4', isBulkAction && 'mt-10')}>
					<Button onClick={handleOnConfirm}>
						{dialogConfig.confirmButtonLabel
							? t(dialogConfig.confirmButtonLabel).toUpperCase()
							: t('dashboard__delete__qr_code__modal_confirm')}
					</Button>
					<Button visual="secondary" onClick={handleClose}>
						{t('dashboard__delete__qr_code__modal_cancel')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
